import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";

// Where the conflict is, in the clinician's terms. The model's own wording covers what was
// said; this line only has to say which two things disagree.
const SOURCE_LABELS = {
  record: "Conflicts with the imported record",
  encounter: "Conflicts with what the patient said earlier",
};

/**
 * Contradictions between what the patient is saying and their chart record, or between two
 * things they have said in this encounter. Rendered inside the Clarifying Questions panel,
 * above the questions themselves, because a contradiction is usually the reason for the
 * question underneath it.
 *
 * Open flags are red. A flag the patient clarifies goes struck through and grey rather than
 * disappearing: the clinician needs to see that it was raised and settled, not be left
 * wondering whether they imagined it. Clicking a row does the same thing by hand, which is
 * the escape hatch for a false positive — with no speaker diarization in the transcript, the
 * model does misread who said what.
 */
const ContradictionList = ({ contradictions = [], onToggleResolved = () => {} }) => {
  if (contradictions.length === 0) return null;

  // Red only while something is actually unresolved. No count: the rows are right underneath
  // and already say how many, and a tally on a clinical warning reads as a score to clear.
  const hasOpen = contradictions.some((c) => !c.resolved);

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 600 }}
        color={hasOpen ? "error.main" : "text.secondary"}
      >
        Possible contradictions
      </Typography>

      <List dense disablePadding>
        {contradictions.map((c) => (
          <ListItem key={c.id} disablePadding sx={{ alignItems: "flex-start" }}>
            <Tooltip title={c.resolved ? "Mark as unresolved" : "Mark as clarified"}>
              <ListItemButton
                onClick={() => onToggleResolved(c.id)}
                aria-label={
                  c.resolved
                    ? `Clarified: ${c.detail}. Mark as unresolved.`
                    : `Contradiction: ${c.detail}. Mark as clarified.`
                }
                sx={{ alignItems: "flex-start", py: 0.5, px: 0.5, borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                  {c.resolved ? (
                    <CheckCircleOutlineRounded fontSize="small" color="disabled" />
                  ) : (
                    <ReportProblemOutlined fontSize="small" color="error" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={c.topic ? `${c.topic}: ${c.detail}` : c.detail}
                  primaryTypographyProps={{
                    variant: "body2",
                    color: c.resolved ? "text.disabled" : "error.main",
                    fontWeight: c.resolved ? 400 : 500,
                    ...(c.resolved && { sx: { textDecoration: "line-through" } }),
                  }}
                  // What settled it, once it is settled — not struck through, because it is
                  // the part that is still true. Falls back to the source line while open.
                  secondary={
                    c.resolved
                      ? c.resolution || "Marked as clarified."
                      : SOURCE_LABELS[c.conflictWith] || null
                  }
                  secondaryTypographyProps={{ variant: "caption", color: "text.secondary" }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ContradictionList;
